import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "chart.js/auto";
import { Bar } from "react-chartjs-2";

import CustomSelect from "../../../Components/common/CustomSelect/CustomSelect";
import TableSpinnerIcon from "../../../Components/Icon-SVG/TableSpinnerIcon";
import {
  KpiTile,
  NoData,
  RankList,
  Widget,
  useThemeColors,
} from "../../../Components/common/DashboardWidgets/DashboardWidgets";
import { RequestIocDashboard } from "../../../store/actions/analysisReportActions";
import IocSearch from "./IocSearch";
import { DEFAULT_WINDOW, IOC_TYPES, WINDOW_OPTIONS, bucketLabel, formatDate } from "./iocUtils";

import "./styles.scss";

// CustomSelect remembers the choice under `${id}LastSelected`; reading the same
// entry here makes the first request use it instead of the default
const PERIOD_SELECT_ID = "iocDashboardWindow";
const DEFAULT_PERIOD_INDEX = WINDOW_OPTIONS.indexOf(DEFAULT_WINDOW);

const storedPeriod = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(`${PERIOD_SELECT_ID}LastSelected`));
    return WINDOW_OPTIONS.find((option) => option.id === stored?.id) || DEFAULT_WINDOW;
  } catch (e) {
    return DEFAULT_WINDOW;
  }
};

const IocDashboard = () => {
  const dispatch = useDispatch();
  const theme = useThemeColors();
  const data = useSelector((state) => state.analysisReports.iocDashboard);
  const loading = useSelector((state) => state.analysisReports.iocDashboardLoading);
  const error = useSelector((state) => state.analysisReports.iocDashboardError);

  const [period, setPeriod] = useState(storedPeriod);
  // A new object each time, so clicking the same item again searches again
  const [searchPreset, setSearchPreset] = useState(null);

  // Keyed by the id, so only a real change of period loads the dashboard again
  const periodId = period.id;
  const loadDashboard = useCallback(() => {
    dispatch(RequestIocDashboard({ filterByLast: periodId }));
  }, [dispatch, periodId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const searchFor = (item) => setSearchPreset({ value: item.name });

  const totals = data?.totals || {};
  const buckets = useMemo(() => data?.timeline?.buckets || [], [data]);
  const hasTimeline = buckets.some((bucket) => IOC_TYPES.some((type) => bucket[type.id]));

  const timelineChart = useMemo(() => {
    const axis = {
      ticks: { color: theme.text, font: { size: 11 } },
      grid: { color: theme.grid, drawTicks: false },
      border: { display: false },
    };
    return {
      data: {
        labels: buckets.map((bucket) => bucketLabel(bucket.key, data?.timeline?.unit)),
        datasets: IOC_TYPES.map((type) => ({
          label: type.label,
          data: buckets.map((bucket) => bucket[type.id] || 0),
          backgroundColor: type.color,
          borderRadius: 3,
          maxBarThickness: 26,
          stack: "iocs",
        })),
      },
      options: {
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: theme.text, boxWidth: 12, boxHeight: 12, useBorderRadius: true },
          },
        },
        scales: {
          x: {
            ...axis,
            stacked: true,
            grid: { display: false },
            ticks: { ...axis.ticks, maxRotation: 0, autoSkipPadding: 12 },
          },
          y: { ...axis, stacked: true, beginAtZero: true, ticks: { ...axis.ticks, precision: 0 } },
        },
      },
    };
  }, [buckets, data, theme]);

  return (
    <div className="mgw-dashboard ioc-dashboard">
      <div className="mgw-dashboard__header">
        <h1>IOC Dashboard</h1>
        <p>
          Indicators of compromise collected from malicious analysis reports.
          {data?.truncated &&
            ` Based on the newest ${data.reportsScanned.toLocaleString()} of ${data.reportsTotal.toLocaleString()} malicious reports.`}
        </p>
        <div className="mgw-dashboard__header__select">
          <p>Filter by last: </p>
          <CustomSelect
            id={PERIOD_SELECT_ID}
            data={WINDOW_OPTIONS}
            select={period}
            setSelected={setPeriod}
            defaultIndex={DEFAULT_PERIOD_INDEX}
            width={140}
          />
          <div className="mgw-dashboard__refresh" onClick={loadDashboard} title="Refresh">
            <TableSpinnerIcon
              className="container-services__spinner"
              customStyle={{ margin: "0", height: "20px" }}
              spin={loading}
            />
          </div>
        </div>
      </div>

      <div className="mgw-dashboard__grid">
        <IocSearch filterByLast={period.id} preset={searchPreset} />

        <div className="mgw-dashboard__kpis">
          <KpiTile
            label="Malicious Reports"
            value={(totals.reports ?? 0).toLocaleString()}
            hint={period.label}
            tone="danger"
          />
          {IOC_TYPES.map((type) => (
            <div key={type.id} className="ioc-dashboard__kpi" style={{ "--ioc-color": type.color }}>
              <KpiTile
                label={type.label}
                value={(totals[type.id] ?? 0).toLocaleString()}
                hint="unique indicators"
              />
            </div>
          ))}
        </div>

        <Widget
          title="Indicators Over Time"
          subtitle={`Unique indicators seen per ${data?.timeline?.unit === "hour" ? "hour" : "day"}`}
          span={8}
        >
          {hasTimeline ? (
            <div className="mgw-chart mgw-chart--tall">
              <Bar data={timelineChart.data} options={timelineChart.options} />
            </div>
          ) : (
            <NoData text={loading ? "Loading..." : error || "No indicators in this period"} />
          )}
        </Widget>

        <Widget
          title="Destination Countries"
          subtitle="Malicious IP addresses per country"
          span={4}
        >
          <RankList items={data?.topCountries} color="#e69b55" />
        </Widget>

        <Widget
          title="Most Contacted Domains"
          subtitle="Reports that reached each domain · click to search"
          span={4}
        >
          <RankList items={data?.topDomains} color="#a77bdb" onSelect={searchFor} />
        </Widget>

        <Widget
          title="Most Contacted IPs"
          subtitle="Reports that reached each address · click to search"
          span={4}
        >
          <RankList items={data?.topIps} color="#e69b55" onSelect={searchFor} />
        </Widget>

        <Widget title="Latest Malicious Samples" subtitle="Indicators each one produced" span={4}>
          {data?.latestSamples?.length ? (
            <ul className="ioc-latest">
              {data.latestSamples.map((sample) => (
                <li key={sample.reportId}>
                  <Link
                    className="ioc-latest__name"
                    to={`/analysis-reports?data=${sample.reportId}`}
                    title={sample.fileName}
                  >
                    {sample.fileName || "Unnamed sample"}
                  </Link>
                  <span className="ioc-latest__date">{formatDate(sample.date)}</span>
                  <span className="ioc-latest__counts">
                    {IOC_TYPES.filter((type) => sample.counts[type.id]).map((type) => (
                      <span key={type.id} className={`ioc-chip ioc-chip--${type.id}`}>
                        {sample.counts[type.id]} {type.short}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <NoData />
          )}
        </Widget>
      </div>
    </div>
  );
};

export default IocDashboard;
